import { ContentfulArticle, ContentfulAsset, ContentfulCompetition, ContentfulYouTube, EntryType } from './types';
import { GraphqlArticle, GraphqlAsset, GraphqlCompetition, GraphqlHeroVideo, GraphqlParentPage } from './gqlTypes';

export const createEntrySys = (spaceId: string | undefined, id: string, contentTypeId: string, createdAt?: string) => ({
  id,
  createdAt,
  contentType: { sys: { id: contentTypeId } },
  space: { sys: { id: spaceId ?? '' } },
});

export const mapAsset = (
  asset: GraphqlAsset | null | undefined,
  spaceId: string | undefined
): ContentfulAsset | undefined => {
  if (!asset?.url) return undefined;
  return {
    sys: createEntrySys(spaceId, asset.sys?.id ?? '', 'asset'),
    fields: {
      title: asset.title ?? '',
      description: asset.description ?? '',
      file: {
        url: asset.url ?? '',
        fileName: asset.fileName ?? '',
        contentType: asset.contentType ?? '',
        details: {
          size: asset.size ?? 0,
          image:
            asset.width && asset.height
              ? {
                  width: asset.width,
                  height: asset.height,
                }
              : undefined,
        },
      },
    },
  } as ContentfulAsset;
};

export const mapHeroVideo = (
  video: GraphqlHeroVideo | null | undefined,
  spaceId: string | undefined
): ContentfulYouTube | undefined => {
  if (!video) return undefined;
  return {
    sys: createEntrySys(spaceId, video.sys?.id ?? '', 'youTube'),
    fields: {
      name: '',
      recommendedVideos: false,
      url: '',
      landscapeCoverImage: mapAsset(video.landscapeCoverImage, spaceId),
    },
  } as ContentfulYouTube;
};

export const mapParentPage = (parentPage: GraphqlParentPage | undefined, spaceId: string | undefined) => {
  if (!parentPage?.categoryPage) return undefined;
  return {
    sys: createEntrySys(spaceId, parentPage.sys?.id ?? '', EntryType.subCategory),
    fields: {
      categoryPage: {
        sys: createEntrySys(spaceId, parentPage.categoryPage.sys?.id ?? '', EntryType.categoryLanding),
        fields: {
          urlSlug: parentPage.categoryPage.urlSlug ?? '',
        },
      },
    },
  } as ContentfulArticle['fields']['parentPage'];
};

export const mapArticleEntry = (item: GraphqlArticle, spaceId: string | undefined): ContentfulArticle => {
  return {
    sys: createEntrySys(
      spaceId,
      item.sys?.id ?? '',
      EntryType.article,
      item.publishedDate ?? item.sys?.publishedAt ?? ''
    ),
    fields: {
      heading: item.heading ?? '',
      intro: item.intro ?? '',
      publishedDate: item.publishedDate ?? undefined,
      urlSlug: item.urlSlug ?? '',
      heroImage: mapAsset(item.heroImage, spaceId),
      heroVideo: mapHeroVideo(item.heroVideo, spaceId),
      parentPage: mapParentPage(item.parentPage, spaceId),
    } as ContentfulArticle['fields'],
  } as ContentfulArticle;
};

export const mapCompetitionEntry = (item: GraphqlCompetition, spaceId: string | undefined): ContentfulCompetition => {
  return {
    sys: createEntrySys(spaceId, item.sys?.id ?? '', EntryType.competition, item.sys?.publishedAt ?? ''),
    fields: {
      heading: item.heading ?? '',
      endDate: item.endDate ?? '',
      urlSlug: item.urlSlug ?? '',
      heroImage: mapAsset(item.heroImage, spaceId) as ContentfulCompetition['fields']['heroImage'],
      parentPage: mapParentPage(item.parentPage, spaceId) as ContentfulCompetition['fields']['parentPage'],
    } as ContentfulCompetition['fields'],
  } as ContentfulCompetition;
};
